/**
 * Ask panel — the center column of the Ask tab: the ask box (sparkle + autosizing
 * textarea + grounded count + ⌘↵), a row of recent-question chips, and the result
 * region which is one of: suggestions (no question yet), the grounded Answer, or
 * the "no grounded answer" honesty state when retrieval came back empty.
 *
 * Presentational — the workspace owns the ask flow + citation-spine state and
 * passes the derived answer + handlers down.
 */
import { Button } from "@d12labs/ui/button";
import { EmptyState } from "@d12labs/ui/empty-state";
import { ChevronRight, History, Layers, RefreshCw, Search, Sparkles } from "lucide-react";
import { useEffect, useRef } from "react";
import type { KbAnswer, KbCitation } from "../types";
import { Answer } from "./answer";

const SUGGESTIONS = [
	"What is the timeline or go-live date?",
	"What is in scope vs out of scope?",
	"What are the key deliverables and costs?",
];

export function AskPanel({
	question,
	onQuestionChange,
	onSubmit,
	isPending,
	docCount,
	recent,
	onPickQuestion,
	answer,
	noGrounded,
	error,
	activeCite,
	onCite,
	onRegenerate,
}: {
	question: string;
	onQuestionChange: (v: string) => void;
	onSubmit: () => void;
	isPending: boolean;
	docCount: number;
	recent: string[];
	onPickQuestion: (q: string) => void;
	answer: KbAnswer | null;
	noGrounded: boolean;
	error: string | null;
	activeCite: number | null;
	onCite: (c: KbCitation) => void;
	onRegenerate: () => void;
}) {
	// Focus the box when the Ask tab mounts (header "Ask" / ⌘K lands here).
	const boxRef = useRef<HTMLTextAreaElement>(null);
	useEffect(() => {
		boxRef.current?.focus();
	}, []);

	return (
		<div className="overflow-y-auto">
			<div className="mx-auto max-w-3xl space-y-6 px-8 py-6">
				{/* Ask box */}
				<div className="rounded-xl border border-hairline-strong bg-surface-1 focus-within:border-primary-focus">
					<div className="flex items-start gap-2.5 px-3.5 pt-3">
						<Sparkles className="mt-0.5 size-4 flex-none text-primary" />
						<textarea
							ref={boxRef}
							rows={2}
							value={question}
							onChange={(e) => onQuestionChange(e.target.value)}
							placeholder="Ask a question about these documents…"
							disabled={isPending}
							onKeyDown={(e) => {
								if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
									e.preventDefault();
									onSubmit();
								}
							}}
							className="flex-1 resize-none bg-transparent text-body-lg text-foreground outline-none placeholder:text-ink-subtle"
						/>
					</div>
					<div className="flex items-center justify-between px-3 pb-2.5 pt-1">
						<span className="inline-flex items-center gap-1.5 text-meta text-ink-subtle">
							<Layers className="size-3" /> Grounded in {docCount} document
							{docCount === 1 ? "" : "s"}
						</span>
						<Button
							size="sm"
							onClick={onSubmit}
							disabled={isPending || question.trim().length === 0}
						>
							{isPending ? (
								<RefreshCw className="size-3.5 animate-spin" />
							) : (
								<Sparkles className="size-3.5" />
							)}
							{isPending ? "Thinking…" : "Ask"}
							<kbd className="ml-1 rounded bg-on-primary/15 px-1 text-[10px]">⌘↵</kbd>
						</Button>
					</div>
				</div>

				{/* Recent questions */}
				{recent.length > 0 ? (
					<div className="flex flex-wrap items-center gap-1.5">
						<span className="inline-flex items-center gap-1 text-meta text-ink-subtle">
							<History className="size-3" /> Recent
						</span>
						{recent.map((q) => (
							<button
								key={q}
								type="button"
								onClick={() => onPickQuestion(q)}
								className="h-6 truncate rounded-full border border-hairline bg-surface-1 px-2.5 text-meta text-ink-muted hover:border-hairline-strong hover:text-foreground"
							>
								{q}
							</button>
						))}
					</div>
				) : null}

				{error ? <p className="text-body-sm text-destructive">{error}</p> : null}

				{/* Result region */}
				{answer ? (
					<Answer
						answer={answer}
						activeCite={activeCite}
						onCite={onCite}
						onRegenerate={onRegenerate}
					/>
				) : noGrounded ? (
					<EmptyState
						icon={Search}
						title="Nothing in these documents covers that"
						body="I couldn't find supporting passages for your question. Try rephrasing, or add a document that contains the answer."
					/>
				) : !isPending ? (
					<div>
						<EmptyState
							icon={Sparkles}
							title={`Ask anything about these ${docCount} document${docCount === 1 ? "" : "s"}`}
							body="Answers are grounded in your indexed sources and cite the exact chunks they came from."
							small
						/>
						<div className="mt-1 space-y-1.5">
							<div className="mb-1 text-eyebrow uppercase text-ink-subtle">Try asking</div>
							{SUGGESTIONS.map((q) => (
								<button
									key={q}
									type="button"
									onClick={() => onPickQuestion(q)}
									className="flex h-9 w-full items-center gap-2 rounded-md border border-hairline bg-surface-1 px-3 text-body-sm text-ink-muted hover:border-hairline-strong hover:text-foreground"
								>
									<Sparkles className="size-3.5 text-primary" />
									<span className="flex-1 text-left">{q}</span>
									<ChevronRight className="size-3.5 text-ink-subtle" />
								</button>
							))}
						</div>
					</div>
				) : null}
			</div>
		</div>
	);
}
